import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { seasonEventAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronLeft, Pencil, Trash2, RefreshCw, ExternalLink, Calendar, Users, Mail, Clock, FileText, Activity } from 'lucide-react';

const EVENT_TYPE_COLORS = {
  'Weekday Event': { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700' },
  'Weekend Event': { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700' },
  'Major Tournament': { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700' },
  'SCGA Team Event': { bg: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-700' },
  'Board Meeting': { bg: 'bg-pink-50', border: 'border-pink-500', text: 'text-pink-700' },
  'Course Maintenance': { bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-700' },
  'Other': { bg: 'bg-gray-50', border: 'border-gray-400', text: 'text-gray-600' }
};

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'registration-open', label: 'Registration Open' },
  { value: 'registration-closed', label: 'Registration Closed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const EVENT_TYPES = [
  'Weekday Event', 'Weekend Event', 'Major Tournament',
  'SCGA Team Event', 'Board Meeting', 'Course Maintenance', 'Other'
];

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isNew = eventId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(isNew);

  // Event data
  const [event, setEvent] = useState(isNew ? {
    name: '',
    description: '',
    eventDate: '',
    startTime: '',
    expectedPlayers: 96,
    eventType: 'Weekday Event',
    season: new Date().getFullYear(),
    status: 'scheduled',
    proshopEmail: 'proshop@balboapark.com',
    notes: ''
  } : null);
  const [editingEvent, setEditingEvent] = useState(null);

  // Related data
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedTournament, setLinkedTournament] = useState(null);

  // Signups sync state
  const [syncingSignups, setSyncingSignups] = useState(false);

  // Signup validation state (for tournament creation)
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const fetchEventData = useCallback(async () => {
    if (isNew) return;

    try {
      setLoading(true);
      const response = await seasonEventAPI.getFullDetail(eventId);
      const data = response.data;

      setEvent(data.event);
      setNotifications(data.notifications?.all || data.notifications || []);
      setActivities(data.activities || []);
      setLinkedTournament(data.linkedTournament || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [eventId, isNew]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  const handleEdit = () => {
    setEditingEvent({
      ...event,
      eventDate: event.eventDate ? new Date(event.eventDate).toISOString().split('T')[0] : ''
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/calendar');
    } else {
      setEditingEvent(null);
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const dataToSave = editingEvent || event;

      if (isNew) {
        const response = await seasonEventAPI.create(dataToSave);
        navigate(`/calendar/events/${response.data.event._id}`);
      } else {
        await seasonEventAPI.update(eventId, dataToSave);
        setEvent({ ...event, ...dataToSave });
        setEditingEvent(null);
        setIsEditing(false);
        fetchEventData();
      }
    } catch (err) {
      console.error('Error saving event:', err);
      setError('Failed to save event: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      await seasonEventAPI.delete(eventId);
      navigate('/calendar');
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event');
    }
  };

  const handleRevertActivity = async (activityId) => {
    if (!window.confirm('Revert this change? The previous values will be restored.')) return;

    try {
      await seasonEventAPI.revertActivity(eventId, activityId);
      fetchEventData();
    } catch (err) {
      console.error('Error reverting activity:', err);
      setError('Failed to revert: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSyncSignups = async () => {
    if (!event?.externalEventId) {
      setError('Cannot sync signups: No external event ID linked');
      return;
    }
    try {
      setSyncingSignups(true);
      setError(null);
      await seasonEventAPI.syncSignups(eventId);
      setValidationResult(null); // Clear validation when signups change
      fetchEventData();
    } catch (err) {
      console.error('Error syncing signups:', err);
      setError('Failed to sync signups: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncingSignups(false);
    }
  };

  const handleValidateSignups = async () => {
    try {
      setValidating(true);
      setError(null);
      const response = await seasonEventAPI.validateSignups(eventId);
      setValidationResult(response.data);
    } catch (err) {
      console.error('Error validating signups:', err);
      setError('Failed to validate signups: ' + (err.response?.data?.error || err.message));
    } finally {
      setValidating(false);
    }
  };

  const handleCreateTournament = () => {
    // Only allow if all signups matched (or no signups)
    if (validationResult && !validationResult.validation.canCreateTournament) {
      setError('Cannot create tournament: Some signups do not match members. Please review unmatched signups.');
      return;
    }

    navigate('/setup', {
      state: {
        fromEvent: {
          seasonEventId: event._id,
          name: event.name,
          date: event.eventDate,
          startTime: event.startTime,
          expectedPlayers: event.syncedSignups?.length || event.expectedPlayers || 0
        }
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    const icons = {
      event_created: '✨',
      event_updated: '✏️',
      tournament_linked: '🏆',
      roster_player_added: '➕',
      roster_player_removed: '➖',
      notification_added: '📧',
      status_changed: '📊',
      field_reverted: '↩️'
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-6 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate('/calendar')}>
            Back to Calendar
          </Button>
        </Card>
      </div>
    );
  }

  const currentData = isEditing ? (editingEvent || event) : event;
  const typeColors = EVENT_TYPE_COLORS[currentData?.eventType] || EVENT_TYPE_COLORS['Other'];


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">
                  {isNew ? 'New Event' : (isEditing ? 'Edit Event' : event?.name)}
                </h1>
                {!isNew && !isEditing && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded border ${typeColors.bg} ${typeColors.text} ${typeColors.border}`}>
                      {event?.eventType}
                    </span>
                    <Badge variant={
                      event?.status === 'scheduled' ? 'secondary' :
                      event?.status === 'registration-open' ? 'success' :
                      event?.status === 'completed' ? 'outline' :
                      event?.status === 'cancelled' ? 'destructive' : 'secondary'
                    }>
                      {event?.status}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || !currentData?.name || !currentData?.eventDate || !currentData?.startTime}
                  >
                    {saving ? 'Saving...' : (isNew ? 'Create Event' : 'Save Changes')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-5xl mx-auto px-4 mt-4">
          <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Accordion type="multiple" defaultValue={['details', 'sync', 'signups', 'tournament', 'communications']} className="space-y-4">

          {/* Event Details Section */}
          <AccordionItem value="details" className="bg-white rounded-lg border shadow-sm">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Event Details</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Event Name *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={currentData?.name || ''}
                      onChange={(e) => setEditingEvent({ ...currentData, name: e.target.value })}
                      placeholder="e.g., Weekly Tournament"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Date *</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.eventDate || ''}
                        onChange={(e) => setEditingEvent({ ...currentData, eventDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Start Time *</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.startTime || ''}
                        onChange={(e) => setEditingEvent({ ...currentData, startTime: e.target.value })}
                        placeholder="e.g., 7:48 AM"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Event Type</label>
                      <select
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.eventType || 'Weekday Event'}
                        onChange={(e) => setEditingEvent({ ...currentData, eventType: e.target.value })}
                      >
                        {EVENT_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Expected Players</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.expectedPlayers || 96}
                        onChange={(e) => setEditingEvent({ ...currentData, expectedPlayers: Number(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Status</label>
                      <select
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.status || 'scheduled'}
                        onChange={(e) => setEditingEvent({ ...currentData, status: e.target.value })}
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Season</label>
                      <select
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        value={currentData?.season || new Date().getFullYear()}
                        onChange={(e) => setEditingEvent({ ...currentData, season: Number(e.target.value) })}
                      >
                        {[2024, 2025, 2026, 2027].map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Pro Shop Email</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={currentData?.proshopEmail || ''}
                      onChange={(e) => setEditingEvent({ ...currentData, proshopEmail: e.target.value })}
                      placeholder="proshop@balboapark.com"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={currentData?.description || ''}
                      onChange={(e) => setEditingEvent({ ...currentData, description: e.target.value })}
                      placeholder="Optional description..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Notes</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={currentData?.notes || ''}
                      onChange={(e) => setEditingEvent({ ...currentData, notes: e.target.value })}
                      placeholder="Internal notes..."
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Date</span>
                    <p className="font-medium">{formatDate(event?.eventDate)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Start Time</span>
                    <p className="font-medium">{event?.startTime}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Expected Players</span>
                    <p className="font-medium">{event?.expectedPlayers}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Season</span>
                    <p className="font-medium">{event?.season}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Pro Shop Email</span>
                    <p className="font-medium">{event?.proshopEmail}</p>
                  </div>
                  {event?.description && (
                    <div className="col-span-2">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Description</span>
                      <p className="text-sm">{event?.description}</p>
                    </div>
                  )}
                  {event?.notes && (
                    <div className="col-span-2">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Notes</span>
                      <p className="text-sm">{event?.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Signups Section */}
          {!isNew && event?.externalEventId && (
            <AccordionItem value="signups" className="bg-white rounded-lg border shadow-sm">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Event Signups</span>
                  </div>
                  <Badge variant={event?.syncedSignups?.length > 0 ? 'success' : 'secondary'}>
                    {event?.syncedSignups?.length || 0} Players
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <p className="text-sm text-muted-foreground">
                    Players registered for this event.
                  </p>
                  <Button onClick={handleSyncSignups} disabled={syncingSignups} size="sm">
                    <RefreshCw className={`h-4 w-4 mr-1 ${syncingSignups ? 'animate-spin' : ''}`} />
                    {syncingSignups ? 'Syncing...' : 'Sync Signups'}
                  </Button>
                </div>

                {event?.syncedSignups?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">#</th>
                          <th className="px-3 py-2 text-left font-medium">Player Name</th>
                          <th className="px-3 py-2 text-left font-medium">GHIN</th>
                          <th className="px-3 py-2 text-left font-medium">Email</th>
                          <th className="px-3 py-2 text-left font-medium">Synced At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {event.syncedSignups.map((signup, index) => (
                          <tr key={signup._id || index} className="hover:bg-muted/30">
                            <td className="px-3 py-2">{index + 1}</td>
                            <td className="px-3 py-2 font-medium">{signup.playerName}</td>
                            <td className="px-3 py-2">{signup.ghin || '-'}</td>
                            <td className="px-3 py-2">{signup.email || '-'}</td>
                            <td className="px-3 py-2 text-muted-foreground">{signup.syncedAt ? formatDateTime(signup.syncedAt) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No signups synced yet. Click "Sync Signups" to pull the current roster.</p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Linked Tournament Section */}
          {!isNew && (
            <AccordionItem value="tournament" className="bg-white rounded-lg border shadow-sm">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Linked Tournament</span>
                  </div>
                  <Badge variant={linkedTournament ? 'success' : 'secondary'}>
                    {linkedTournament ? 'Linked' : 'Not Linked'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {linkedTournament ? (
                  <div className="space-y-3">
                    <h4 className="font-medium">{linkedTournament.name}</h4>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>Format: {linkedTournament.format || 'N/A'}</span>
                      <span>Players: {linkedTournament.playerCount || 0}</span>
                      <span>Status: {linkedTournament.status}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" asChild>
                        <Link to={`/editor/${linkedTournament._id}`}>View Tournament</Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/editor/${linkedTournament._id}/roster`}>View Roster</Link>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center py-4">
                      <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">No tournament linked to this event.</p>
                    </div>

                    {/* Signup Validation Section */}
                    {event?.syncedSignups?.length > 0 && (
                      <div className="border rounded-lg p-4 bg-muted/20">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-sm">Signup Validation</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleValidateSignups}
                            disabled={validating}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            {validating ? 'Validating...' : 'Validate Signups'}
                          </Button>
                        </div>

                        {!validationResult && (
                          <p className="text-sm text-muted-foreground">
                            Click "Validate Signups" to check if all {event.syncedSignups.length} signups match members in the database.
                          </p>
                        )}

                        {validationResult && (
                          <div className="space-y-3">
                            {/* Summary */}
                            <div className={`p-3 rounded-lg ${validationResult.validation.allMatched ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-lg ${validationResult.validation.allMatched ? 'text-green-600' : 'text-red-600'}`}>
                                  {validationResult.validation.allMatched ? '✓' : '✗'}
                                </span>
                                <span className={`font-medium ${validationResult.validation.allMatched ? 'text-green-800' : 'text-red-800'}`}>
                                  {validationResult.validation.allMatched ? 'All signups matched!' : 'Some signups unmatched'}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {validationResult.validation.matchedCount} of {validationResult.validation.totalSignups} matched
                                ({validationResult.validation.matchRate}%)
                              </p>
                            </div>

                            {/* Unmatched Signups List */}
                            {validationResult.unmatchedSignups?.length > 0 && (
                              <div>
                                <h5 className="text-sm font-medium text-red-700 mb-2">
                                  Unmatched Signups ({validationResult.unmatchedSignups.length}):
                                </h5>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                  {validationResult.unmatchedSignups.map((signup, idx) => (
                                    <div key={idx} className="text-sm bg-red-50 px-2 py-1 rounded flex justify-between">
                                      <span className="font-medium">{signup.originalName}</span>
                                      <span className="text-muted-foreground text-xs">
                                        Parsed: {signup.parsedFirstName} {signup.parsedLastName}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-red-600 mt-2">
                                  These signups must be added to the member database before creating a tournament.
                                </p>
                              </div>
                            )}

                            {/* Matched Signups Collapsible */}
                            {validationResult.matchedSignups?.length > 0 && (
                              <details className="text-sm">
                                <summary className="cursor-pointer text-green-700 font-medium">
                                  Matched Signups ({validationResult.matchedSignups.length})
                                </summary>
                                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                                  {validationResult.matchedSignups.map((signup, idx) => (
                                    <div key={idx} className="bg-green-50 px-2 py-1 rounded flex justify-between">
                                      <span>{signup.originalName}</span>
                                      <span className="text-muted-foreground text-xs">
                                        → {signup.member.fullName} (GHIN: {signup.member.ghin})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Create Tournament Button */}
                    <div className="text-center">
                      <Button
                        size="sm"
                        onClick={handleCreateTournament}
                        disabled={event?.syncedSignups?.length > 0 && (!validationResult || !validationResult.validation.canCreateTournament)}
                      >
                        Create Tournament
                      </Button>
                      {event?.syncedSignups?.length > 0 && !validationResult && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Validate signups first to enable tournament creation
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Communications Section */}
          {!isNew && (
            <AccordionItem value="communications" className="bg-white rounded-lg border shadow-sm">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Communications</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{notifications.length} notifications</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {notifications.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {notifications.map(notif => (
                      <div key={notif._id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <span className="font-medium text-sm">{notif.notificationType}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            Scheduled: {formatDateTime(notif.scheduledFor)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            notif.status === 'sent' ? 'success' :
                            notif.status === 'pending' ? 'secondary' :
                            notif.status === 'skipped' ? 'outline' : 'secondary'
                          }>
                            {notif.status}
                          </Badge>
                          {notif.sentAt && (
                            <span className="text-xs text-muted-foreground">Sent: {formatDateTime(notif.sentAt)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">No scheduled notifications for this event.</p>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to="/scheduled-emails">
                    <Mail className="h-4 w-4 mr-1" />
                    Manage Scheduled Emails
                  </Link>
                </Button>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Activity Timeline Section */}
          {!isNew && (
            <AccordionItem value="activity" className="bg-white rounded-lg border shadow-sm">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Activity Timeline</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{activities.length} activities</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map(activity => (
                      <div key={activity._id} className={`flex gap-3 p-3 rounded-lg ${activity.isReverted ? 'bg-muted/50 opacity-60' : 'bg-muted/30'}`}>
                        <span className="text-lg">{getActivityIcon(activity.activityType)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">{activity.description}</span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(activity.performedAt)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">by {activity.performedBy}</span>
                            <Badge variant="outline" className="text-xs">{activity.source}</Badge>
                            {activity.isReverted && <Badge variant="secondary" className="text-xs">Reverted</Badge>}
                          </div>
                          {activity.fieldChanges?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {activity.fieldChanges.map((change, idx) => (
                                <div key={idx} className="text-xs bg-background px-2 py-1 rounded">
                                  <span className="font-medium">{change.field}:</span>
                                  <span className="text-muted-foreground ml-1">{String(change.previousValue || 'empty')}</span>
                                  <span className="mx-1">→</span>
                                  <span className="text-foreground">{String(change.newValue || 'empty')}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {!activity.isReverted && activity.fieldChanges?.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevertActivity(activity._id)}
                            title="Revert this change"
                          >
                            ↩️
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  );
};

export default EventDetail;
